'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase';

type SignUpResponse = {
  user: User | null;
  session: Session | null;
  requiresEmailConfirmation?: boolean;
};

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ data: { user: User | null; session: Session | null } | null; error: any }>;
  signUp: (email: string, password: string, redirectTo?: string, captchaToken?: string) => Promise<{ data: SignUpResponse | null; error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: any }>;
  /** Re-send the signup confirmation email (Supabase rate-limits to one per minute per address). */
  resendConfirmation: (email: string, redirectTo?: string, captchaToken?: string) => Promise<{ error: any }>;
};

/** Where the confirmation link lands. Shared by signUp and resendConfirmation so both emails agree. */
function buildCallbackUrl(redirectTo?: string): string {
  const currentOrigin = typeof window !== 'undefined'
    ? window.location.origin
    : process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:9002';
  return redirectTo
    ? `${currentOrigin}/auth/callback?next=${encodeURIComponent(redirectTo)}`
    : `${currentOrigin}/auth/callback`;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** User ids this tab already asked the server to link, so it runs once per tab. */
const linkAttempted = new Set<string>();

/**
 * Attach anything this person bought as a guest (program enrollment, call credits, kit)
 * to their account. The email confirm link uses the implicit flow, so the session arrives
 * in the URL hash and /auth/callback never sees a ?code= to link with; this is the path
 * that actually runs for those buyers, on whatever page they land. The server checks for
 * unclaimed rows first, so for everyone else it is one cheap lookup per tab. When it does
 * link something, reload once so the page shows the program, credits and intake form.
 */
function linkGuestPurchasesOnce(session: Session | null) {
  const user = session?.user;
  if (!user?.email_confirmed_at || !session?.access_token || linkAttempted.has(user.id)) return;
  linkAttempted.add(user.id);
  fetch('/api/link-pending-purchases', {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((result) => {
      if (result?.enrollmentLinked || result?.linkedCount > 0) window.location.reload();
    })
    .catch((err) => console.error('Linking guest purchases failed (non-blocking):', err));
}

export function SupabaseAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // Check active sessions and sets the user
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') linkGuestPurchasesOnce(session);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (error) {
        // Check if the error is due to unconfirmed email
        if (error.message.toLowerCase().includes('email not confirmed')) {
          // Return a specific error code for unconfirmed email
          return { 
            data: null, 
            error: { 
              ...error, 
              code: 'unconfirmed_email',
              message: 'Please check your email to confirm your account before signing in.'
            } 
          };
        }
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { 
        data: null, 
        error: { 
          message: 'An unexpected error occurred during sign in',
          code: 'unknown_error'
        } 
      };
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string, redirectTo?: string, captchaToken?: string) => {
    try {
      const callbackUrl = buildCallbackUrl(redirectTo);
      console.log('Setting up signup with callback URL:', callbackUrl);
        
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: callbackUrl,
          ...(captchaToken ? { captchaToken } : {}),
        },
      });

      if (error) {
        console.error('Sign up error:', error);
        return { data: null, error };
      }

      // If email confirmation is required, we'll get a session but user email might not be confirmed yet
      if (data.user && data.session) {
        return { 
          data: { 
            ...data,
            requiresEmailConfirmation: !data.user.email_confirmed_at
          }, 
          error: null 
        };
      }

      // If we get here, email confirmation was sent
      return { 
        data: { 
          ...data,
          requiresEmailConfirmation: true
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Unexpected error during sign up:', error);
      return { 
        data: null, 
        error: { 
          message: 'An unexpected error occurred during sign up',
          code: 'unknown_error'
        } 
      };
    }
  };

  // Sign out
  const signOut = async () => {
    await supabase.auth.signOut();
    router.push('/');
  };

  // Reset password
  const resetPassword = async (email: string, captchaToken?: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
      captchaToken,
    });
    return { data, error };
  };

  const resendConfirmation = async (email: string, redirectTo?: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: buildCallbackUrl(redirectTo),
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    resendConfirmation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useSupabaseAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};
